import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Camera, Video, PartyPopper, Check } from "lucide-react";

interface ServicesStepProps {
  services: string[];
  onServicesChange: (services: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const serviceOptions = [
  {
    id: "photography",
    label: "Photography",
    icon: Camera,
    description: "Capture every precious moment"
  },
  {
    id: "videography",
    label: "Videography",
    icon: Video,
    description: "Cinematic wedding films"
  },
  {
    id: "photobooth",
    label: "Photo Booth",
    icon: PartyPopper,
    description: "Fun memories for your guests"
  }
];

const ServicesStep = ({ 
  services, 
  onServicesChange, 
  onNext, 
  onBack 
}: ServicesStepProps) => {
  const toggleService = (serviceId: string) => {
    if (services.includes(serviceId)) {
      onServicesChange(services.filter(s => s !== serviceId));
    } else {
      onServicesChange([...services, serviceId]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex flex-col items-center text-center"
    >
      <div className="mb-6">
        <Camera className="w-12 h-12 text-primary mx-auto mb-4" />
        <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-2">
          What are you interested in?
        </h2>
        <p className="text-muted-foreground">
          Select all that apply — we'll customize your quote
        </p>
      </div>

      <div className="w-full max-w-md grid gap-3 mb-8">
        {serviceOptions.map((service) => {
          const isSelected = services.includes(service.id);
          const Icon = service.icon;
          
          return (
            <motion.button
              key={service.id}
              onClick={() => toggleService(service.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                relative p-4 rounded-xl border-2 text-left transition-all
                ${isSelected 
                  ? "border-primary bg-primary/5" 
                  : "border-border hover:border-primary/50 bg-card"
                }
              `}
            >
              <div className="flex items-center gap-4">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  ${isSelected ? "bg-primary text-primary-foreground" : "bg-muted"}
                `}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">{service.label}</h3>
                  <p className="text-sm text-muted-foreground">{service.description}</p>
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-6 h-6 rounded-full bg-primary flex items-center justify-center"
                  >
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex gap-3 w-full max-w-md">
        <Button
          variant="outline"
          onClick={onBack}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={services.length === 0}
          className="flex-1"
        >
          Continue
        </Button>
      </div>
    </motion.div>
  );
};

export default ServicesStep;
